import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import html2canvas from "html2canvas";
import * as htmlToImage from "html-to-image";

import { DataElement } from "src/app/shared/models/common";
import {
  RXForm,
  RXItem,
  RXLine,
  RXTooth,
  ToothTypeEnum,
} from "src/app/shared/models/order.model";
import { AppState } from "src/app/shared/redux/store";
import { BroshService } from "src/app/shared/services/brosh.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { Store } from "@ngrx/store";
import { curRXForm, rxItems } from "src/app/shared/ngrx/ngrx-selectors";
import { CommonModule } from "@angular/common";
import { TooltipModule } from "primeng/tooltip";
import { FormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";
import { SelectButtonModule } from "primeng/selectbutton";

export class SelectTypeEnum {
  static CROWN: string = "CROWN";
  static BRIDGE: string = "BRIDGE";
  static IMPLANT: string = "IMPLANT";
  static EXTRACT: string = "EXTRACT";
  static JAW: string = "JAW";
}

@Component({
  selector: "teeth-edit",
  templateUrl: "./teeth-edit.component.html",
  styleUrls: ["./teeth-edit.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    TranslateModule,
    FormsModule,
    SelectModule,
    SelectButtonModule,
  ],
})
export class TeethEditComponent {
  @Input() readOnly: boolean;
  @Output() onDeleteRow = new EventEmitter();
  @Output() onCreateRow = new EventEmitter();
  @Output() onChange = new EventEmitter();

  line?: RXLine;
  hoverLine?: RXLine;
  teeth: RXTooth[] = [];
  selectType: SelectTypeEnum = SelectTypeEnum.CROWN;
  item: RXItem;
  prevItem: RXItem;
  stage: DataElement;
  stages: DataElement[] = [];
  startBridge: RXTooth;

  get rxItems(): RXItem[] {
    return this.store
      .selectSignal(rxItems)()
      .filter((i) => i.activeSw);
  }

  get form(): RXForm {
    return this.store.selectSignal(curRXForm)();
  }

  get showCrownBridgeToolbar(): boolean {
    return (
      this.item?.allowSingleSw &&
      this.item?.allowSplintedSw &&
      this.item.lineType != "REMOVABLE"
    );
  }

  get showRemovableSingleManyToolbar(): boolean {
    return (
      this.item?.allowSingleSw &&
      this.item?.allowSplintedSw &&
      this.item.lineType == "REMOVABLE"
    );
  }

  get tableLines(): RXLine[] {
    return this.form.lines?.filter((l) => !l.dirtySw) ?? [];
  }

  get helpText(): string {
    if (!this.item) {
      return this.translateService.instant("translate.rx.helpText.chooseItem");
    } else if (this.item.teethQtyType == "TEETH") {
      return this.translateService.instant(
        "translate.rx.helpText.selectTeethAndFinish"
      );
    } else if (this.item.teethQtyType == "QTY") {
      return this.translateService.instant(
        "translate.rx.helpText.qtyItemWithoutTeethSelect"
      );
    }
  }

  get errorText(): string {
    let err;
    if (!this.line) return null;

    // one line per same item and stage
    if (
      this.form.lines.find(
        (l) =>
          l.lineId != this.line?.lineId &&
          l.rxItem?.id == this.item.id &&
          l.stage?.id == this.stage?.id
      )
    ) {
      err = this.translateService.instant("translate.rx.alertItemStageExist");
    }
    // max one removable per tooth
    else if (
      this.form.lines.find(
        (l) =>
          l.lineId != this.line?.lineId &&
          this.item.lineType == "REMOVABLE" &&
          l.rxItem?.lineType == "REMOVABLE" &&
          l.rxItem?.id != "TEMP_PART_DENT" &&
          this.item.id != "TEMP_PART_DENT" &&
          l.rxItem?.id != "TEMP_WHOLE_DENT" &&
          this.item.id != "TEMP_WHOLE_DENT" &&
          l.teeth.find(
            (t) => t.type && this.teeth.find((x) => x.type && x.id == t.id)
          )
      )
    ) {
      err = this.translateService.instant(
        "translate.rx.alertMaxRemovablePerTooth"
      );
    } // max one fixed per tooth (except PMMA)
    else if (
      this.form.lines.find(
        (l) =>
          l.lineId != this.line?.lineId &&
          this.item.lineType == "FIXED" &&
          l.rxItem?.lineType == "FIXED" &&
          (!["PMMA", "TC_PREP", "TC_EGGSHELL"].includes(l.rxItem?.id)) &&
          (!["PMMA", "TC_PREP", "TC_EGGSHELL"].includes(this.item?.id)) &&
          l.teeth.find(
            (t) => t.type && this.teeth.find((x) => x.type && x.id == t.id)
          )
      )
    ) {
      err = this.translateService.instant("translate.rx.alertMaxFixedPerTooth");
    }

    return err;
  }

  constructor(
    private readonly store: Store,
    private translateService: TranslateService,
    private sharedService: SharedService,
    private broshService: BroshService
  ) {
    this.initFullRXTeethDisplay();
  }

  ngOnInit(): void { }

  onLineSelect(line: RXLine) {
    if (this.readOnly) return;

    if (this.line == line) {
      this.line = null;
      this.clearSelection();
    } else {
      this.line = line;
      this.initLineDisplay(this.line);
    }
  }

  initFullRXTeethDisplay() {
    this.teeth = new RXLine().teeth;
    this.initExtractAndImplantsDisplay();

    this.form.lines.forEach((l) => {
      l.teeth.forEach((t) => {
        let tooth = this.teeth.find((e) => e.id == t.id);
        if (t.type) {
          tooth.type = t.type;
          tooth.splintedGroup = t.splintedGroup;
        }
      });
    });
  }

  initExtractAndImplantsDisplay() {
    this.form.extractTeeth.forEach((t) => {
      let tooth = this.teeth.find((e) => e.id == t.id);
      if (tooth) {
        tooth.extractSw = true;
        tooth.toothSeq = t.toothSeq;
      }
    });

    this.form.implantTeeth.forEach((t) => {
      let tooth = this.teeth.find((e) => e.id == t.id);
      if (tooth) {
        tooth.implantSw = true;
        tooth.toothSeq = t.toothSeq;
      }
    });
  }

  initLineDisplay(line: RXLine) {
    this.teeth = line.teeth;
    this.initExtractAndImplantsDisplay();
    this.item = line.rxItem;
    this.initSelectType();
    this.stages = this.item.stages;
    this.stage = this.stages.find((s) => s.id == line.stage.id);
  }

  onItemChanged() {
    this.stage = null;
    this.stages = this.item.stages;
    if (this.stages.length) {
      this.stage = this.item.stages[0];
    }

    this.initSelectType();

    if (!this.line) {
      this.line = new RXLine();
    }

    let keepTeeth = false;
    if (
      !this.line.lineId &&
      this.prevItem &&
      this.prevItem.allowJawSw == this.item?.allowJawSw &&
      this.prevItem.allowSingleSw == this.item?.allowSingleSw &&
      this.prevItem.allowSplintedSw == this.item?.allowSplintedSw
    ) {
      keepTeeth = true;
    }

    if (!keepTeeth) {
      this.teeth = new RXLine().teeth;
    }

    this.line.teeth = this.teeth;

    this.initExtractAndImplantsDisplay();
    this.line.rxItem = this.item;

    this.prevItem = this.item;
  }

  initSelectType() {
    if (this.showCrownBridgeToolbar || this.item.allowSingleSw) {
      this.selectType = SelectTypeEnum.CROWN;
    } else if (this.item.allowSplintedSw) {
      this.selectType = SelectTypeEnum.BRIDGE;
    } else if (this.item.allowJawSw) {
      this.selectType = SelectTypeEnum.JAW;
    }
  }

  onToothClick(tooth: RXTooth) {
    if (
      this.readOnly ||
      (!this.item &&
        (this.selectType == SelectTypeEnum.BRIDGE ||
          this.selectType == SelectTypeEnum.CROWN)) ||
      this.item?.teethQtyType == "QTY"
    ) {
      return;
    }

    switch (this.selectType) {
      case SelectTypeEnum.IMPLANT:
        if (!tooth.extractSw) tooth.implantSw = !tooth.implantSw;
        if (tooth.implantSw) {
          this.broshService.setupImplantToothOpt(tooth);
          if (this.form.implantTeeth.length)
            tooth.implantCompany = this.form.implantTeeth[0].implantCompany;
          // tooth.dirtySw = true;
          this.form.implantTeeth.push(tooth);
          this.onCreateRow.emit();
        } else {
          let idx = this.form.implantTeeth.findIndex((t) => t.id == tooth.id);
          if (idx != -1) {
            this.form.implantTeeth.splice(idx, 1);
            this.onDeleteRow.emit();
          }
        }
        this.initExtractAndImplantsDisplay();
        this.selectType = SelectTypeEnum.IMPLANT;
        break;

      case SelectTypeEnum.EXTRACT:
        if (!tooth.implantSw) tooth.extractSw = !tooth.extractSw;
        if (tooth.extractSw) {
          this.form.extractTeeth.push(tooth);
        } else {
          let idx = this.form.extractTeeth.findIndex((t) => t.id == tooth.id);
          if (idx != -1) {
            this.form.extractTeeth.splice(idx, 1);
          }
        }
        this.initExtractAndImplantsDisplay();
        this.onChange.emit();
        break;

      case SelectTypeEnum.CROWN:
        if (tooth.type) {
          this.cancelToothBridgeSelection(tooth);
        } else {
          tooth.type = ToothTypeEnum.SINGLE_UNIT;
          tooth.dirtySw = false;
          let maxGroup = Math.max(
            ...this.teeth.map((o) => o.splintedGroup ?? 0)
          );
          tooth.splintedGroup = maxGroup + 1;
        }
        this.line.teeth = this.teeth;
        break;

      case SelectTypeEnum.BRIDGE:
        if (!tooth.type) {
          if (!this.startBridge) {
            tooth.dirtySw = true;
            this.startBridge = tooth;
          } else {
            this.createBridge();
          }
        } else {
          this.cancelToothBridgeSelection(tooth);
        }
        break;

      case SelectTypeEnum.JAW:
        if (tooth.type) {
          this.cancelToothBridgeSelection(tooth);
        } else {
          this.createBridge();
        }

        break;
      default:
        break;
    }
  }

  createBridge() {
    if (this.validateBridge()) {
      let maxGroup = Math.max(...this.teeth.map((o) => o.splintedGroup ?? 0));
      this.teeth
        .filter((t) => t.dirtySw)
        .map((t) => {
          t.splintedGroup = maxGroup + 1;
          t.type = ToothTypeEnum.SPLINTED;
          t.dirtySw = false;
        });
      this.line.teeth = this.teeth;
      this.startBridge = null;
    } else {
      this.teeth.map((t) => (t.dirtySw = false));
      this.startBridge = null;
    }
  }

  cancelToothBridgeSelection(tooth: RXTooth) {
    if (tooth.type == ToothTypeEnum.SPLINTED) {
      this.teeth
        .filter((t) => t.splintedGroup == tooth.splintedGroup)
        .forEach((t) => {
          t.type = null;
          t.dirtySw = false;
          t.splintedGroup = null;
        });
    } else if (tooth.type == ToothTypeEnum.SINGLE_UNIT) {
      tooth.type = null;
      tooth.dirtySw = false;
      tooth.splintedGroup = null;
    }
  }

  validateBridge() {
    let dirty = this.teeth.filter((t) => t.dirtySw);
    return (
      dirty.length > 1 &&
      // same jaw
      (dirty.every((t) => t.jaw == 0) || dirty.every((t) => t.jaw == 1)) &&
      // sequence
      dirty.every((t, i) => i == 0 || t.sortOrder == dirty[i - 1].sortOrder + 1)
    );
  }

  onMouseEnter(tooth: RXTooth) {
    if (this.startBridge) {
      if (tooth == this.startBridge) return;

      if (this.startBridge.jaw == tooth.jaw) {
        let between = this.teeth.filter((t) => t.jaw == tooth.jaw);
        if (tooth.sortOrder > this.startBridge.sortOrder) {
          between = between.filter(
            (t) =>
              t.sortOrder >= this.startBridge.sortOrder &&
              t.sortOrder <= tooth.sortOrder
          );
        } else if (tooth.sortOrder < this.startBridge.sortOrder) {
          between = between.filter(
            (t) =>
              t.sortOrder <= this.startBridge.sortOrder &&
              t.sortOrder >= tooth.sortOrder
          );
        }

        between.map((t) => (t.dirtySw = true));
      }
    } else if (this.selectType == SelectTypeEnum.JAW) {
      this.teeth.forEach((t) => (t.dirtySw = t.jaw == tooth.jaw));
    }
  }

  onMouseLeave(tooth: RXTooth) {
    if (this.startBridge) {
      this.teeth
        .filter((t) => t.id != this.startBridge.id)
        .map((t) => (t.dirtySw = false));
    } else if (this.selectType == SelectTypeEnum.JAW) {
      this.teeth
        .filter((t) => t.jaw == tooth.jaw)
        .forEach((t) => (t.dirtySw = false));
    }
  }

  onSelectTypeChange(type: SelectTypeEnum) {
    if (
      this.selectType == type &&
      (this.selectType == SelectTypeEnum.EXTRACT ||
        this.selectType == SelectTypeEnum.IMPLANT)
    ) {
      if (this.showCrownBridgeToolbar || this.item?.allowSingleSw) {
        this.selectType = SelectTypeEnum.CROWN;
      } else if (this.item?.allowSplintedSw) {
        this.selectType = SelectTypeEnum.BRIDGE;
      } else {
        this.selectType = null;
      }
    } else {
      this.selectType = type;
    }

    this.teeth.map((t) => (t.dirtySw = false));
    this.startBridge = null;
  }

  confirmEnabled() {
    if (
      !this.errorText &&
      this.item &&
      (this.stage || !this.item.stages.length) &&
      (this.teeth.some((t) => t.type) ||
        (this.item?.teethQtyType == "QTY" && this.line.qty))
    )
      return true;
    else return false;
  }

  addLine() {
    this.teeth.map((t) => (t.dirtySw = false));
    this.line.rxItem = this.item;
    this.line.stage = this.stage;
    this.line.teeth = this.teeth;
    this.line.toothDisplayText = this.line.teeth
      .filter((t) => t.type)
      .map((t) => t.id)
      .join(",");

    if (!this.line.lineId) {
      this.line.dirtySw = true;
      this.line.lineId =
        this.form.lines.length == 0
          ? 1
          : Math.max(...this.form.lines.map((o) => o.lineId ?? 0)) + 1;
      this.form.lines.push(this.line);
    }

    this.onCreateRow.emit();
    this.clearSelection();
  }

  clearSelection() {
    this.item = null;
    this.prevItem = null;
    this.stage = null;
    this.line = null;
    this.selectType = null;
    this.startBridge = null;
    this.initFullRXTeethDisplay();
  }

  isOutlined(tooth: RXTooth) {
    if (tooth.dirtySw) return true;

    let lines: RXLine[] = this.line ? [this.line] : this.form.lines;

    return lines.some((l) => {
      let idx = l.teeth.findIndex((t) => t.id == tooth.id);
      return l.rxItem?.displayGroup == "BLUE" && l.teeth[idx].type;
    });
  }

  isLeftBridge(tooth: RXTooth, displayGroup: string): boolean {
    let lines: RXLine[] = this.line ? [this.line] : this.form.lines;

    return lines.some((l) => {
      let idx = l.teeth.findIndex((t) => t.id == tooth.id);
      return (
        l.rxItem?.displayGroup == displayGroup &&
        l.teeth[idx].type == ToothTypeEnum.SPLINTED &&
        (idx == 0 ||
          l.teeth[idx - 1].splintedGroup != l.teeth[idx].splintedGroup)
      );
    });
  }

  isRightBridge(tooth: RXTooth, displayGroup: string): boolean {
    let lines: RXLine[] = this.line ? [this.line] : this.form.lines;

    return lines.some((l) => {
      let idx = l.teeth.findIndex((t) => t.id == tooth.id);
      return (
        l.rxItem?.displayGroup == displayGroup &&
        l.teeth[idx].type == ToothTypeEnum.SPLINTED &&
        (idx == l.teeth.length - 1 ||
          l.teeth[idx + 1].splintedGroup != l.teeth[idx].splintedGroup)
      );
    });
  }

  isMiddleBridge(tooth: RXTooth, displayGroup: string): boolean {
    let lines: RXLine[] = this.line ? [this.line] : this.form.lines;

    return lines.some((l) => {
      let idx = l.teeth.findIndex((t) => t.id == tooth.id);
      return (
        l.rxItem?.displayGroup == displayGroup &&
        l.teeth[idx].type == ToothTypeEnum.SPLINTED &&
        idx > 0 &&
        idx < l.teeth.length - 1 &&
        l.teeth[idx - 1].splintedGroup == l.teeth[idx].splintedGroup &&
        l.teeth[idx + 1].splintedGroup == l.teeth[idx].splintedGroup
      );
    });
  }

  isSingleGreen(tooth: RXTooth) {
    let lines: RXLine[] = this.line ? [this.line] : this.form.lines;

    return lines.some((l) => {
      let idx = l.teeth.findIndex((t) => t.id == tooth.id);
      return (
        l.rxItem?.displayGroup == "GREEN" &&
        l.teeth[idx].type == ToothTypeEnum.SINGLE_UNIT
      );
    });
  }

  async toDataUrl(): Promise<string> {
    let data = document.getElementById("teeth_cross");
    // let canvas = await html2canvas(data);
    // return canvas.toDataURL("image/jpeg", 1.0);

    return htmlToImage.toSvg(data, {
      skipFonts: true,
      pixelRatio: 2,
    });
  }

  deleteRow(line: RXLine) {
    let validationStr = this.translateService.instant(
      "translate.rx.deleteRowValidation"
    );

    this.sharedService.emitPopupchReceivedEvent({
      title: "translate.alert.infoTitle",
      icon: "exclamation-triangle",
      subTitle: validationStr,
      keep: false,
      approve: () => {
        let idx = this.form.lines.indexOf(line);
        this.form.lines.splice(idx, 1);
        this.onDeleteRow.emit();
        this.clearSelection();
        this.initFullRXTeethDisplay();
      },
    });
  }

  getToothTooltip(tooth: RXTooth) {
    if (this.item) return null;

    let content = "";
    let lines: RXLine[] = this.line ? [this.line] : this.form.lines;
    lines = lines.filter((l) =>
      l.teeth.find((t) => t.id == tooth.id && t.type)
    );
    if (lines.length) {
      content = "<ol class='list-type-none'>";
      lines.forEach((l) => {
        content += "<li>" + l.rxItem.text + "</li>";
      });
      content += "</ol>";
    }
    return content;
  }

  onStageChangeFromTable(event) {
    event?.originalEvent?.stopPropagation();
    this.onCreateRow.emit();
  }
}
